import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import { useSkillsApi } from '../apis/skills-apis';
import { Skill } from '../types';
import { useToast } from "../components/ui/Toast";
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigationGuard } from '../hooks/useNavigationGuard';
import SkillForm, { SkillFormRef } from './SkillForm';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_SKILL_TIMEOUT } from '../config/api-config';

export const AddSkillPage: React.FC = () => {
  const formRef = useRef<SkillFormRef>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const skillsApi = useSkillsApi();
  const { user } = useAuth();
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [existingSkillNames, setExistingSkillNames] = useState<string[]>([]);
  const backButtonClicked = useRef(false);
  const historyStateAdded = useRef(false);
  const [isSuccessfullySaved, setIsSuccessfullySaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newSkill, setNewSkill] = useState<Skill>({
    skillId: '',
    skillName: '',
    skillDescription: '',
    skillUrlEndpoint: '',
    skillAuthentication: '',
    skillParameters: {
      parameters: [],
      testMode: true
    },
    skillOutput: {
      parameters: {
        output: {
          description: '',
          type: ''
        }
      }
    },
    skillAnnotation: '',
    ownerId: user?.sub || '',
    skillTimeout: DEFAULT_SKILL_TIMEOUT,
    skillTags: [],
    tags: [],
    isCore: false,
    isAuthPresent: false,
    accessControl: {
      viewAccessType: 'Owner Only',
      viewAllowedUsers: [],
      editAccessType: 'Owner Only',
      editAllowedUsers: []
    }
  });

  // Determine if there are unsaved changes by checking if any required fields are filled
  const hasUnsavedChanges = !isSuccessfullySaved && Boolean(
    newSkill.skillName ||
    newSkill.skillDescription ||
    newSkill.skillUrlEndpoint ||
    newSkill.skillAuthentication ||
    newSkill.skillParameters.parameters.length > 0 ||
    newSkill.skillOutput.parameters.output?.description ||
    newSkill.skillAnnotation ||
    (newSkill.skillTags && newSkill.skillTags.length > 0)
  );

  const { 
    showNavigationWarning, 
    guardNavigation, 
    confirmNavigation, 
    cancelNavigation 
  } = useNavigationGuard(hasUnsavedChanges);

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      return (
        hasUnsavedChanges && 
        currentLocation.pathname !== nextLocation.pathname
      );
    }
  );

  // Handle blocked navigation
  useEffect(() => {
    if (blocker.state === "blocked") {
      guardNavigation(blocker.location.pathname);
    }
  }, [blocker, guardNavigation]);

  // Handle browser back button and page refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handlePopState = () => {
      if (hasUnsavedChanges) {
        guardNavigation('/marketplace/skills');
      } else {
        navigate('/marketplace/skills');
      }
    };
  
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, navigate]);

  useEffect(() => {
    const fetchExistingSkillNames = async () => {
      try {
        const getAllSkills = true;
        const skills = await skillsApi.getSkills(getAllSkills);
        setExistingSkillNames(skills.map(skill => skill.skillName));
      } catch (error) {
        console.error('Error fetching existing skill names:', error);
        addToast({
          title: "Error",
          description: "Failed to fetch existing skill names",
          variant: "destructive",
          duration: 4000
        });
      }
    };

    fetchExistingSkillNames();
  }, []);

  const handleSubmit = async () => {
    const isValid = await formRef.current?.validateForm();
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    setIsSaving(true);
  
    try {
      const createdSkill = await skillsApi.createSkill(newSkill);
      setIsSuccessfullySaved(true);
      addToast({
        title: "Success",
        description: "Skill " + newSkill.skillName + " has been successfully created",
        variant: "default",
        duration: 4000
      });
      
      // Navigate to the test page for the newly created skill
      navigate(`/marketplace/skills/test/${createdSkill.skillId}`);
  
    } catch (error) {
      console.error('Error creating skill:', error);
      addToast({
        title: "Error",
        description: "Failed to create skill: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      guardNavigation('/marketplace/skills');
    } else {
      navigate('/marketplace/skills');
    }
  };

  const handleConfirmWithBlocker = () => {
    setIsSuccessfullySaved(true); // Reset the unsaved changes flag
    confirmNavigation();
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };

  const handleCancelWithBlocker = () => {
    cancelNavigation();
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };

  return (
    <>
      <SkillForm
        ref={formRef}
        skill={newSkill}
        onSkillChange={(skill: Skill) => {
          setNewSkill(skill);
          setIsSuccessfullySaved(false);
        }}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={false}
        setIsReadOnly={setIsReadOnly}
        existingSkillNames={existingSkillNames}
        currentUserId={user?.sub || ''}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmWithBlocker}
        onCancel={handleCancelWithBlocker}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default AddSkillPage;